
import React, { useState, useContext } from 'react';
import { AppContext, supabase } from '../contexts/AppContext';
import { useUI } from '../contexts/UIContext';

const LoginScreen: React.FC = () => {
  const { login, loginError, setUnauthenticatedView } = useContext(AppContext);
  const { registerSocio, registrationError } = useContext(AppContext);
  const ui = useUI();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nombre, setNombre] = useState('');
  const [clubId, setClubId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      return;
    }
    setIsLoading(true);
    await login(email, password);
    setIsLoading(false);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !nombre) return;
    setIsLoading(true);
    const parsedClubId = clubId ? Number(clubId) : undefined;
    await registerSocio(nombre, email, password, parsedClubId);
    setIsLoading(false);
  };

  const handleSendMagicLink = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!email) {
      ui.showToast('error', 'Ingresá un email válido para enviar el link.');
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({ email, options: { emailRedirectTo: window.location.origin } });
      if (error) {
        ui.showToast('error', `No se pudo enviar el link: ${error.message || error}`);
      } else {
        ui.showToast('success', 'Link de acceso enviado. Revisá tu email.');
      }
    } catch (err: any) {
      ui.showToast('error', `Error: ${err?.message || err}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!email) {
      ui.showToast('error', 'Ingresá el email asociado a tu cuenta.');
      return;
    }
    setIsLoading(true);
    try {
      // Preferir resetPasswordForEmail si está disponible en la versión del cliente
      if (typeof (supabase.auth as any).resetPasswordForEmail === 'function') {
        const res: any = await (supabase.auth as any).resetPasswordForEmail(email);
        if (res?.error) ui.showToast('error', `No se pudo enviar el email de recuperación: ${res.error.message || res.error}`);
        else ui.showToast('success', 'Email de recuperación enviado. Revisá tu casilla.');
      } else {
        // Fallback: usar magic link para permitir acceso y luego el socio puede cambiar su contraseña
        const { error } = await supabase.auth.signInWithOtp({ email, options: { emailRedirectTo: window.location.origin } });
        if (error) ui.showToast('error', `No se pudo enviar el link: ${error.message || error}`);
        else ui.showToast('success', 'Link enviado. Revisá tu email.');
      }
    } catch (err: any) {
      ui.showToast('error', `Error: ${err?.message || err}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center items-center p-4">
      <div className="text-center mb-8">
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); setUnauthenticatedView('landing'); }}
          className="inline-block"
        >
          <img src="/logo.png" alt="Tu Club" className="h-16 mx-auto mb-4" />
        </a>
        <p className="text-gray-500">Iniciá sesión para gestionar tu club</p>
      </div>

      <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-sm">

        <form onSubmit={isRegistering ? handleRegister : handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <div className="mt-1">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>

          {isRegistering && (
            <div>
              <label htmlFor="nombre" className="block text-sm font-medium text-gray-700">Nombre</label>
              <div className="mt-1">
                <input id="nombre" name="nombre" required value={nombre} onChange={(e) => setNombre(e.target.value)} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm" />
              </div>
              <label htmlFor="clubId" className="block text-sm font-medium text-gray-700 mt-2">ID del club (opcional)</label>
              <div className="mt-1">
                <input id="clubId" name="clubId" value={clubId} onChange={(e) => setClubId(e.target.value)} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm" />
              </div>
            </div>
          )}

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Contraseña
            </label>
            <div className="mt-1">
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>

          {(loginError || registrationError) && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md" role="alert">
              <p className="font-bold">Error</p>
              <p>{loginError || registrationError}</p>
            </div>
          )}

          <div>
            <button type="submit" disabled={isLoading} className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400">
              {isLoading ? (isRegistering ? 'Registrando...' : 'Ingresando...') : (isRegistering ? 'Registrarme' : 'Ingresar')}
            </button>
          </div>
          <div className="mt-3">
            <button
              onClick={handleSendMagicLink}
              disabled={isLoading}
              className="w-full inline-flex justify-center items-center py-2 px-4 border border-transparent rounded-md text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 disabled:opacity-60"
            >
              {isLoading ? 'Enviando link...' : 'Acceder sin contraseña'}
            </button>
          </div>
        </form>

        <div className="mt-6 text-center text-sm text-gray-500">
          <div className="mt-2">
            <a href="#" onClick={(e) => { e.preventDefault(); setUnauthenticatedView('onboarding'); }} className="text-xs text-gray-400">O iniciar onboarding para crear club</a>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LoginScreen;